import { Button, Modal } from "@omnitok/ui";
import { AlertTriangle, CheckCircle2, Rocket } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useEffect, useState } from "react";

import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import { useT } from "@/i18n";
import { Disclosure } from "@pervasivemind/admin-ui";
import { draftsApi } from "@/lib/builder-api";
import type { AiHomeViewsCheck, ValidationResult } from "@/types";

import { BrokenViewsWarning } from "./BrokenViewsWarning";

interface PublishModalProps {
  open: boolean;
  onClose: () => void;
  draft: UseBuilderDraft;
}

/**
 * Confirm + validate before publishing.
 *
 * On open we fire ``validate()`` to surface errors before the user
 * commits. The user can publish only when validation passes; success
 * auto-closes the modal after a short delay so they see the green
 * confirmation.
 *
 * Saved AI Home views (2026-10-26): once the config validates, every saved
 * view reading this report is tried with its AI Home time rule (the one in
 * the editor). Publish waits for that answer; views that would stop
 * working, or a check that could not run, turn the button into "Publish
 * anyway", which sends ``confirm_broken_views`` (the server answers 409
 * without it).
 */
export function PublishModal({
  open,
  onClose,
  draft,
}: PublishModalProps): JSX.Element {
  const { t } = useT();
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [publishResult, setPublishResult] = useState<ValidationResult | null>(null);
  const [views, setViews] = useState<AiHomeViewsCheck | null>(null);
  const [checkingViews, setCheckingViews] = useState(false);

  // Validate on open, reset on close.
  useEffect(() => {
    if (!open) {
      setResult(null);
      setPublishResult(null);
      setViews(null);
      return;
    }
    let cancelled = false;
    setValidating(true);
    draft
      .validate()
      .then(async (r) => {
        if (cancelled) return;
        setResult(r);
        if (!r.valid) return;
        setCheckingViews(true);
        try {
          const found = await draftsApi.checkAiHomeViews(draft.key, draft.ai);
          if (!cancelled) setViews(found);
        } catch (err: unknown) {
          // Not run is said as such: publishing stays possible, knowingly.
          const message = err instanceof Error ? err.message : String(err);
          if (!cancelled) setViews({ checked: null, broken: [], already_broken: [], check_error: message });
        } finally {
          if (!cancelled) setCheckingViews(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setResult({
          valid: false,
          errors: [err instanceof Error ? err.message : "Validation failed"],
          warnings: [],
        });
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
    // draft.validate is stable enough; we revalidate on every open intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const viewsWarn = views !== null && (views.broken.length > 0 || views.check_error !== undefined);
  const handlePublish = async (): Promise<void> => {
    const r = await draft.publish(viewsWarn);
    setPublishResult(r);
    if (r?.valid) {
      window.setTimeout(() => onClose(), 1200);
    }
  };

  const showResult = publishResult ?? result;
  const canPublish =
    !validating && !checkingViews && showResult?.valid === true && publishResult === null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("publish.title")}
      size="lg"
      footer={
        <>
          <Button ghost onClick={onClose}>
            {t("publish.close")}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handlePublish()}
            disabled={!canPublish || draft.publishing || draft.saving}
            loading={draft.publishing}
            leftIcon={!draft.publishing ? <Rocket size={14} /> : undefined}
            data-testid="publish-confirm"
          >
            {viewsWarn ? t("publish.confirmAnyway") : t("publish.confirm")}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-neutral-700">
        <p>
          {t("publish.body")}{" "}
          <span className="font-mono text-xs text-neutral-500">{draft.key}</span>
        </p>

        {validating && (
          <div className="flex items-center gap-2 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
            <Spinner size={14} ariaLabel={t("publish.validating")} />
            {t("publish.validating")}
          </div>
        )}

        {showResult && !validating && (
          <ValidationView
            result={showResult}
            success={publishResult?.valid === true}
          />
        )}

        {publishResult === null && (
          <BrokenViewsWarning found={views} checking={checkingViews} live={false} />
        )}
      </div>
    </Modal>
  );
}

interface ValidationViewProps {
  result: ValidationResult;
  success: boolean;
}

function ValidationView({ result, success }: ValidationViewProps): JSX.Element {
  const { t } = useT();
  if (success) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success-dark">
        <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
        <span>{t("publish.success")}</span>
      </div>
    );
  }
  if (result.valid) {
    return (
      <>
        <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success-dark">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
          <span>{t("publish.passed")}</span>
        </div>
        {result.warnings.length > 0 && (
          <Disclosure
            className="border-warning/40 bg-warning/10"
            summary={`${result.warnings.length} ${t("publish.warningsTitle")}`}
          >
            <ul className="mt-2 list-disc pl-5">
              {result.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </Disclosure>
        )}
      </>
    );
  }
  return (
    <div className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-xs text-error-dark">
      <div className="mb-1 flex items-center gap-1.5 font-medium">
        <AlertTriangle size={14} />
        {t("publish.errorsTitle")}
      </div>
      <ul className="list-disc pl-5">
        {result.errors.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
      {result.warnings.length > 0 && (
        <div className="mt-2 border-t border-error/30 pt-2">
          <p className="font-medium">{t("publish.warnings")}</p>
          <ul className="list-disc pl-5">
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
